import Quill from 'quill';
import ImageUploader from 'quill-image-uploader';

/**
 * Wraps `quill-image-uploader` so every pasted/uploaded image is placed on a line of its own.
 *
 * The default module drops the image inline, at the caret, which leaves images glued to the
 * surrounding text (e.g. `some text[image]more text`). Before the image is inserted we normalise
 * the caret so that:
 *  - text before the caret stays on the previous line;
 *  - text after the caret is pushed to the line below;
 *  - the caret ends up on the line under the image, ready for the next input.
 */
class CustomImageUploader extends ImageUploader {
  /** Inserts the temporary base64 preview shown while the file is being uploaded. */
  insertBase64Image(url: string, promiseUpload: Promise<string>) {
    const { quill } = this;
    const LoadingImage = Quill.import('formats/imageBlot');
    const placeholderBlotName = LoadingImage?.blotName ?? 'imageBlot';

    const index = this.isolateRangeInOwnLine();

    this.placeholderDelta = quill.insertEmbed(index, placeholderBlotName, `${url}`, 'user');

    // Track the placeholder itself. The original module tracks `leaf.next`, which points at the
    // next line once the image owns its line, making it replace the wrong content on upload.
    const [placeholderLeaf] = quill.getLeaf(index);
    this.placeholderLeafs.set(promiseUpload, placeholderLeaf);

    this.range = { index: index + 1, length: 0 };

    // Leave the caret on the line below the image, so typing continues under it.
    quill.setSelection(index + 2, 0, 'user');
  }

  /**
   * Moves the caret to an empty line, creating the surrounding line breaks when needed.
   *
   * @returns the index the image will be inserted at.
   */
  private isolateRangeInOwnLine() {
    const { quill } = this;

    const currentRange = this.range ?? quill.getSelection();
    let index = currentRange?.index ?? Math.max(quill.getLength() - 1, 0);
    const selectionLength = currentRange?.length ?? 0;

    // Replace the selected content first, so the image does not sit next to the leftovers.
    if (selectionLength > 0) {
      quill.deleteText(index, selectionLength, 'user');
    }

    const [line, offsetInLine] = quill.getLine(index);

    if (line) {
      // Something is typed before the caret: break the line so the image starts a new one.
      if (offsetInLine > 0) {
        quill.insertText(index, '\n', 'user');
        index += 1;
      }

      // `line.length()` counts the trailing newline, hence the extra -1.
      const contentAfterCaret = line.length() - 1 - offsetInLine;

      // Something is typed after the caret: push it down so the image keeps the line to itself.
      if (contentAfterCaret > 0) {
        quill.insertText(index, '\n', 'user');
      }
    }

    // The image would close the document: add an empty line so typing continues below it.
    if (index >= quill.getLength() - 1) {
      quill.insertText(index, '\n', 'user');
    }

    quill.setSelection(index, 0, 'user');

    return index;
  }
}

export default CustomImageUploader;
